/**
 * Helpers to infer RPA / auth-on-file fetch failures from sparse order status payloads.
 *
 * @module checkRpaError
 */
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../data-model/medicalPaOrdersModel";
import { AuthOnFileRpaStatus } from "../../../enums/authOnFileAuthStatus";

/**
 * Checks whether the auth-on-file RPA encountered an error by
 * verifying that both `entries`/`auth_entries` and `auth_on_file` status are missing.
 *
 * @param authOnFileDetails - Auth-on-file data (may be undefined before load)
 * @param orderDetails - General order details (may be undefined before load)
 * @returns `true` when neither auth entries nor an auth-on-file status exist
 */
export const checkRpaError = (
  authOnFileDetails?: AuthOnFile,
  orderDetails?: GeneralOrderDetails,
) => {
  const authEntries =
    authOnFileDetails?.entries ?? authOnFileDetails?.auth_entries;
  const authOnFileStatusExists = orderDetails?.status?.meta?.auth_on_file;

  if (authOnFileStatusExists === undefined && authEntries === undefined) {
    return true;
  }
  return false;
};

/**
 * @param data - General order with optional nested `status.auth_on_file`
 * @returns The inner `auth_on_file.status` string when present; otherwise `undefined`
 */
export const checkStatus = (data: GeneralOrderDetails) => {
  if (data.status?.auth_on_file_completion_status) {
    return data.status?.auth_on_file_completion_status;
  }
};

/**
 * Determines whether the auth-on-file RPA is currently running
 * (queued, initiated, or in progress) for the provided order.
 *
 * @param order - General order details (may be undefined before load)
 * @returns `true` when the RPA status indicates an in-flight run
 */
export const isRpaInFlight = (order?: GeneralOrderDetails) => {
  const status = order?.status?.auth_on_file_completion_status;
  return (
    status === AuthOnFileRpaStatus.IN_PROGRESS ||
    status === AuthOnFileRpaStatus.INITIATED ||
    status === AuthOnFileRpaStatus.QUEUED
  );
};

/**
 * @param data - General order with optional nested `status.auth_on_file`
 * @returns RPA error classification when `auth_on_file` exists; otherwise `undefined`
 */
export const getErrorType = (data: GeneralOrderDetails) => {
  if (data.status?.meta?.auth_on_file) {
    return data.status?.meta?.auth_on_file?.type ?? "";
  }
};
